import { Component, CUSTOM_ELEMENTS_SCHEMA, inject, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  Router,
  NavigationEnd,
  RouterOutlet,
  ActivatedRoute,
} from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { FooterComponent } from './footer';
import { HeaderComponent } from './header';
import { SidebarComponent } from './sidebar';
import { ThemeCustomizerComponent } from './theme-customizer';
import { NgClass } from '@angular/common';
import { AppService } from '../services/app.service';
import { AuthService } from '../modules/auth/services/auth/auth.service';
import type { CpPortalAccessSummary } from '../modules/auth/services/auth/auth.model';
import { ToastService } from '../core/services/toast/toast.service';
import { FirebaseNotificationInitService } from '../core/services/firebase/firebase-notification-init.service';
import { CpSubscriptionTrialService } from '../core/services/subscription-trial/cp-subscription-trial.service';
import { SubscriptionUpgradeModalComponent } from '../shared/components/subscription-upgrade-modal/subscription-upgrade-modal.component';

@Component({
  selector: 'app-root',
  templateUrl: './app-layout.html',
  standalone: true,
  imports: [
    NgClass,
    ThemeCustomizerComponent,
    SidebarComponent,
    HeaderComponent,
    RouterOutlet,
    FooterComponent,
    SubscriptionUpgradeModalComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppLayout implements OnInit {
  store: any;
  showTopButton = false;
  currentModule: string = '';
  headerClass = '';
  isRefreshingAccess = false;
  private readonly authService = inject(AuthService);
  private readonly toast = inject(ToastService);
  private readonly firebaseNotificationInit = inject(
    FirebaseNotificationInitService,
  );
  readonly subscriptionTrial = inject(CpSubscriptionTrialService);

  constructor(
    public translate: TranslateService,
    public storeData: Store<any>,
    private readonly service: AppService,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
  ) {}

  ngOnInit() {
    this.initStore();
    this.currentModule = this.activatedRoute.snapshot?.url[0]?.path || '';
    this.authService.refreshPortalAccessSummary();
    this.subscriptionTrial.refresh();
    // Post-auth FCM setup — must not block layout; failures are swallowed.
    void this.firebaseNotificationInit.initialize();
    this.initAnimation();
    window.addEventListener('scroll', () => {
      if (
        document.body.scrollTop > 50 ||
        document.documentElement.scrollTop > 50
      ) {
        this.showTopButton = true;
      } else {
        this.showTopButton = false;
      }
    });
  }

  ngOnDestroy() {
    window.removeEventListener('scroll', () => {});
  }

  initAnimation() {
    this.service.changeAnimation();
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.service.changeAnimation();
      }
    });

    const ele: any = document.querySelector('.animation');
    ele.addEventListener('animationend', () => {
      this.service.changeAnimation('remove');
    });
  }

  initStore() {
    this.storeData
      .select(d => d.index)
      .subscribe(d => {
        this.store = d;
      });
  }

  goToTop() {
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }

  get isPortalLocked(): boolean {
    return !this.authService.hasFullPortalAccess();
  }

  get portalLockReason(): 'none' | 'pending' | 'rejected' {
    return this.authService.portalLockReason();
  }

  refreshAccessStatus(): void {
    if (this.isRefreshingAccess) {
      return;
    }
    this.isRefreshingAccess = true;
    this.authService.refreshPortalAccessSummary$().subscribe({
      next: (summary: CpPortalAccessSummary | null) => {
        this.isRefreshingAccess = false;
        if (!summary) {
          return;
        }
        if (summary.hasApprovedBuilderAccess) {
          this.toast.success('Builder access approved. Full portal unlocked.');
          void this.router.navigateByUrl('/dashboard');
          return;
        }
        this.toast.info(
          summary.portalAccessMode === 'builders_only'
            ? 'Status refreshed. Builder approval is still pending.'
            : 'Status refreshed.',
        );
      },
      error: () => {
        this.isRefreshingAccess = false;
        this.toast.error('Could not refresh approval status.');
      },
    });
  }
}
